import { useCallback } from "react";

import { PingKind, type ConnectivityState, type EquipmentAvailabilityDay } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Chart } from "@eisox/design-system";
import { colors } from "@eisox/tokens";
import { defineChart, rect, text, type ChartPoint } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tickX } from "@tanstack/charts/tick";

import { m } from "~/paraglide/messages";

import { CONNECTIVITY_STATES, PING_KINDS } from "../connectivity-history.constant";
import { dayProgress, formatAvailability, formatDuration } from "../helpers/connectivity-history.helper";

const ROW_HEIGHT = 36;
const AXIS_HEIGHT = 32;
const BAND_PADDING = 0.35;
const PERCENT_LABEL_WIDTH = 80;
const HOUR_TICKS = Array.from({ length: 8 }, (_, step) => step / 8);

type DaysChartKey = ConnectivityState | PingKind.EARLY;

const SERIES: Record<DaysChartKey, { label: () => string; color: string }> = {
  ...CONNECTIVITY_STATES,
  [PingKind.EARLY]: PING_KINDS[PingKind.EARLY],
};

const SERIES_BY_KEY = new Map<string, { label: () => string }>(Object.entries(SERIES));

interface DaysChartDatum {
  day: EquipmentAvailabilityDay;
  from: number;
  to: number;
  key?: DaysChartKey;
  start?: string;
  end?: string;
}

interface ConnectivityDaysChartProps {
  days: EquipmentAvailabilityDay[];
  selectedDayStart?: string;
  onSelectDay: (dayStart: string) => void;
  timezone: string;
}

const ConnectivityDaysChart: React.FC<ConnectivityDaysChartProps> = ({
  days,
  selectedDayStart,
  onSelectDay,
  timezone,
}) => {
  const definitionOf = useCallback(
    (hiddenSeries: readonly string[]) => {
      const isShown = ({ key }: DaysChartDatum) => key === undefined || !hiddenSeries.includes(key);
      const formatDay = (start: string) => dayjs(start).tz(timezone).format("ddd L");
      const formatTime = (instant: string) => dayjs(instant).tz(timezone).format("LT");
      const dayOf = ({ day }: DaysChartDatum) => day.start;

      const tracks: DaysChartDatum[] = days.map(day => ({ day, from: 0, to: 1 }));
      const segments: DaysChartDatum[] = days.flatMap(day =>
        day.segments.map(({ start, end, state }) => ({
          day,
          from: dayProgress(day, start),
          to: dayProgress(day, end),
          key: state,
          start,
          end,
        })),
      );
      const earlyPings: DaysChartDatum[] = days.flatMap(day =>
        day.earlyPings.map(start => {
          const at = dayProgress(day, start);
          return { day, from: at, to: at, key: PingKind.EARLY, start };
        }),
      );

      return defineChart({
        marks: [
          rect(tracks, { x1: "from", x2: "to", y: dayOf, fill: colors.primary.gray, radius: 4 }),
          rect(segments.filter(isShown), { x1: "from", x2: "to", y: dayOf, color: "key", inset: 0 }),
          tickX(earlyPings.filter(isShown), {
            x: "from",
            y: dayOf,
            color: "key",
            strokeWidth: 2,
            span: 1 - BAND_PADDING,
          }),
          decorative(
            rect(
              tracks.filter(({ day }) => day.start === selectedDayStart),
              {
                x1: "from",
                x2: "to",
                y: dayOf,
                fillOpacity: 0,
                stroke: colors.primary.darkBlue,
                strokeWidth: 2,
                radius: 4,
              },
            ),
          ),
          decorative(
            text(tracks, {
              x: "to",
              y: dayOf,
              text: ({ day }) => formatAvailability(day.availability),
              anchor: "start",
              dx: 12,
              fill: colors.primary.darkBlue,
            }),
          ),
        ],
        color: {
          domain: Object.keys(SERIES),
          range: Object.values(SERIES).map(({ color }) => color),
        },
        margin: { right: PERCENT_LABEL_WIDTH },
        scales: {
          x: {
            scale: scaleLinear().domain([0, 1]),
            grid: Chart.grid,
            axis: {
              ...Chart.axis,
              ticks: {
                ...Chart.axis.ticks,
                values: HOUR_TICKS,
                format: (progress: number) =>
                  dayjs()
                    .startOf("day")
                    .add(progress * 24, "hour")
                    .format("LT"),
              },
            },
          },
          y: {
            scale: scaleBand()
              .domain(days.map(({ start }) => start))
              .padding(BAND_PADDING),
            axis: { ...Chart.axis, ticks: { ...Chart.axis.ticks, format: formatDay } },
          },
        },
        tooltip: {
          ...Chart.tooltip,
          content: ([point]) => {
            if (!point) return { rows: [] };
            const { day, key, start, end } = point.datum;
            if (!key || !start) {
              return {
                title: formatDay(day.start),
                rows: [{ label: m.connectivity_history_availability(), value: formatAvailability(day.availability) }],
              };
            }
            return {
              title: formatDay(day.start),
              rows: [
                {
                  label: SERIES[key].label(),
                  value: end
                    ? `${formatTime(start)} – ${formatTime(end)} · ${formatDuration(dayjs(end).diff(start))}`
                    : formatTime(start),
                  color: SERIES[key].color,
                },
              ],
            };
          },
        },
      });
    },
    [days, selectedDayStart, timezone],
  );

  return (
    <Chart.Root
      definition={definitionOf}
      ariaLabel={m.connectivity_history_availability()}
      height={days.length * ROW_HEIGHT + AXIS_HEIGHT}
      onSelect={(point: ChartPoint<DaysChartDatum> | null) => point && onSelectDay(point.datum.day.start)}
      className="w-full cursor-pointer"
    >
      <Chart.Legend label={key => SERIES_BY_KEY.get(key)?.label() ?? key} className="justify-start" />
    </Chart.Root>
  );
};

ConnectivityDaysChart.displayName = "ConnectivityDaysChart";

export { ConnectivityDaysChart };
